// Stored files (support-chat attachments, avatars, deposit invoices) - Trello NDt1OCeB.
//
// The backend now serves GET /kyc/document/:name, /avatar/:name and /file/download/:name only to a
// session that may read them (the owning client, or a manager). An <img src> or <a href> cannot
// carry the Authorization header, so these files are fetched with the client's token and handed to
// the browser as a blob: URL. No token is ever put into a URL.
//
// Backends that have not been updated yet still serve a tokenless URL, and there a client token can
// be REFUSED for a file it owns (the old check read the owner from parseInt of the filename, which a
// uuid-named invoice fails). So when the authenticated fetch is refused, each helper falls back to
// the old plain URL. Against an updated backend that fallback is refused too, so it gives nothing
// away; it only keeps this bundle working on every backend while they are rolled out one by one.
import {REST_ENDPOINT} from './config'
import RestService from './rest.service'

const FILE_ROUTE = /^\/(kyc\/document|avatar|file\/download)\//
const cache = new Map()

export function isFilePath(path) {
    return typeof path === 'string' && FILE_ROUTE.test(path)
}

export function legacyUrl(path) {
    return REST_ENDPOINT + path
}

export function fetchFileBlob(path) {
    const token = RestService.getToken()
    return fetch(REST_ENDPOINT + path, {
        method: 'GET',
        headers: token ? {Authorization: token} : {},
        cache: 'no-store',
        credentials: 'omit'
    }).then(res => {
        if (!res.ok) {
            throw new Error('HTTP ' + res.status)
        }
        // A refusal is the legacy envelope: HTTP 200 with {status: 403, error}. No stored file is
        // served as JSON, so JSON here is always an error.
        const ct = res.headers.get('Content-Type') || ''
        if (ct.indexOf('application/json') !== -1) {
            return res.json().then(body => {
                throw new Error((body && body.error) || 'FORBIDDEN')
            })
        }
        return res.blob()
    })
}

// One blob URL per file and session, kept for the life of the page: the same avatar is drawn beside
// every chat message, and the same attachment re-renders whenever the message list refreshes.
export function fileObjectUrl(path) {
    const key = RestService.getToken() + ' ' + path
    if (!cache.has(key)) {
        const p = fetchFileBlob(path).then(blob => URL.createObjectURL(blob))
        p.catch(() => cache.delete(key))
        cache.set(key, p)
    }
    return cache.get(key)
}

// Open in a new tab. The tab is opened inside the click so a popup blocker lets it through, then
// pointed at the file once the bytes arrive.
export function openFile(path) {
    const win = window.open('', '_blank')
    return fileObjectUrl(path)
        .then(url => {
            if (win) {
                win.location.href = url
            } else {
                window.open(url, '_blank')
            }
        })
        .catch(() => {
            if (win) {
                win.location.href = legacyUrl(path)
            } else {
                window.open(legacyUrl(path), '_blank')
            }
        })
}

// Save under `filename`.
export function downloadFile(path, filename) {
    const save = href => {
        const anchor = document.createElement('a')
        anchor.href = href
        anchor.download = filename || path.split('/').pop()
        anchor.target = '_blank'
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
    }
    return fetchFileBlob(path)
        .then(blob => {
            const url = URL.createObjectURL(blob)
            save(url)
            setTimeout(() => URL.revokeObjectURL(url), 60 * 1000)
        })
        .catch(() => save(legacyUrl(path) + '?download'))
}

// v-auth-src="path": like :src, but a stored-file path (/kyc/document/..., /avatar/...) is loaded
// with the session token. Anything else (a static image, '') is set as a plain src.
function apply(el, value) {
    el.__authSrc = value
    if (!isFilePath(value)) {
        if (value) {
            el.src = value
        } else {
            el.removeAttribute('src')
        }
        return
    }
    fileObjectUrl(value)
        .then(url => {
            if (el.__authSrc === value) el.src = url
        })
        .catch(() => {
            if (el.__authSrc === value) el.src = legacyUrl(value)
        })
}

export const authSrc = {
    bind(el, binding) {
        apply(el, binding.value)
    },
    update(el, binding) {
        if (binding.value !== binding.oldValue) {
            apply(el, binding.value)
        }
    }
}
